import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { tableIdState } from "../recoil/atoms/tableAtom";
import { clearCache } from "../services/apiCacheHelper.ts";

const TABLE_ID_STORAGE_KEY = "tableId";

const RESERVED_ROUTES = new Set([
  "login",
  "cart",
  "search",
  "account",
]);

export const parseTableId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  let normalizedValue;

  try {
    normalizedValue = decodeURIComponent(
      String(value)
    ).trim();
  } catch {
    return null;
  }

  if (
    !normalizedValue ||
    normalizedValue.toLowerCase() === "null" ||
    normalizedValue.toLowerCase() === "undefined" ||
    normalizedValue.length > 100 ||
    !/^[A-Za-z0-9_-]+$/.test(normalizedValue)
  ) {
    return null;
  }

  return normalizedValue;
};

export const getStoredTableId = () => {
  try {
    return parseTableId(
      window.sessionStorage.getItem(
        TABLE_ID_STORAGE_KEY
      )
    );
  } catch {
    return null;
  }
};

export const storeTableId = (value) => {
  const tableId = parseTableId(value);

  if (!tableId) {
    return null;
  }

  try {
    window.sessionStorage.setItem(
      TABLE_ID_STORAGE_KEY,
      tableId
    );

    return tableId;
  } catch {
    return null;
  }
};

export const getTableId = (
  pathname = window.location.pathname
) => {
  const pathSegment = pathname
    ? pathname.split("/").filter(Boolean)[0] || ""
    : "";

  const pathId = RESERVED_ROUTES.has(
    pathSegment.toLowerCase()
  )
    ? null
    : parseTableId(pathSegment);

  return pathId || getStoredTableId();
};

export const useTableId = () => {
  const location = useLocation();

  const [tableId, setTableId] =
    useRecoilState(tableIdState);

  const currentTableId = getTableId(
    location.pathname
  );

  useEffect(() => {
    if (currentTableId) {
      storeTableId(currentTableId);

      if (currentTableId !== tableId) {
        setTableId(currentTableId);
      }
    }
  }, [
    currentTableId,
    tableId,
    setTableId,
  ]);

  return currentTableId || tableId;
};

export const clearTableData = () => {
  try {
    window.sessionStorage.removeItem(
      TABLE_ID_STORAGE_KEY
    );
  } catch {
    // Storage may be unavailable.
  }

  clearCache();
};

export default useTableId;